import type { HTMLAttributes } from 'react';
import React, { useEffect, useLayoutEffect } from 'react';

import type { RenderModelPage } from '@aline/common';
import { isRenderModelPageEntry, Queries } from '@aline/common';
import type { NodeEntry, Path } from 'slate';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { useSyncCursorData } from 'src/editor/helpers/useLoadDoc';
import { useWalkthroughActive } from 'src/editor/toolbars/editor-app-bars/helpers';
import { ClientTransforms } from 'src/editor/transforms';
import { IS_MOBILE } from 'src/helpers/environment';
import { useGetEditorUserState, useScrollToURLDocComment } from 'src/hooks';
import { Document, EDITOR_TO_DOCUMENT_ELEMENT, KeyboardInputHandler } from 'src/layout-engine';
import type { AlineClientEditor } from 'src/layout-engine/hooks';
import {
  useEditor,
  useEditorLoading,
  useEditorOnChangeCounter,
  useSlateSelector,
} from 'src/layout-engine/hooks';
import { makeStyles } from 'tss-react/mui';

import { DocumentOutline } from './document-outline';
import { EditorFindPopper } from './EditorFindPopper';
import { EditorLoadingPage } from './EditorLoadingPage';

const useStyles = makeStyles()(() => ({
  layoutEngine: {
    height: '100%',
    width: '100%',
  },
}));

export function LayoutEngine(props: HTMLAttributes<HTMLDivElement>): JSX.Element | null {
  const editor = useEditor();
  const { classes } = useStyles();
  const loading = useEditorLoading();
  const mouseDownClick = useEditorTabStateSelector(state => state.click.mouseDownClick);
  const {
    click: { setMouseDownClick },
  } = useEditorTabStateActions();
  const { isInSigningCeremony } = useGetEditorUserState();
  const { walkThroughActive } = useWalkthroughActive();
  const hideOutline = IS_MOBILE && (isInSigningCeremony || walkThroughActive);

  useSyncCursorData();
  useScrollToURLDocComment();
  useSetAlineDocPageWidthPx();

  useLayoutEffect(() => {
    if (!mouseDownClick) return;

    const { skipScrollSelectionIntoView = false } = mouseDownClick;

    let isDragging = true;
    let clientX: number | null = null;
    let clientY: number | null = null;
    let initialClientY: number | null = null;
    let lastScrolledAt = performance.now();

    function handleMouseUp(event: MouseEvent) {
      event.preventDefault();
      setMouseDownClick(null);
      if (!skipScrollSelectionIntoView) ClientTransforms.scrollSelectionIntoView(editor);
      isDragging = false;
    }

    function handleMouseMove(event: MouseEvent) {
      clientX = event.clientX;
      clientY = event.clientY;
      if (initialClientY === null) initialClientY = clientY;
    }

    function scrollTowardsMouse() {
      if (clientX !== null && clientY !== null && initialClientY !== null && isDragging) {
        const now = performance.now();
        const deltaTimeMs = now - lastScrolledAt;
        lastScrolledAt = now;

        const scrolled = ClientTransforms.scrollTowardsMouse(editor, {
          clientY,
          deltaTimeMs,
          initialClientY,
        });

        // If the page was scrolled, update the selection even though the mouse did not move
        if (scrolled) {
          const closestPageInfo = getClosestPageToPointer(editor, { clientX, clientY });

          if (closestPageInfo) {
            const { pointerX, pointerY, rMPageEntry } = closestPageInfo;
            ClientTransforms.mouseMoveSelect(editor, { pointerX, pointerY, rMPageEntry });
          }
        }
      }

      if (isDragging) requestAnimationFrame(scrollTowardsMouse);
    }

    requestAnimationFrame(scrollTowardsMouse);

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
      isDragging = false;
    };
  }, [editor, mouseDownClick, setMouseDownClick]);

  if (loading || !editor.renderModel) return <EditorLoadingPage />;

  return (
    <div data-layout-engine className={classes.layoutEngine} {...props}>
      <EditorFindPopper />
      <KeyboardInputHandler />
      {!hideOutline && <DocumentOutline />}
      <RenderModelDocument />
    </div>
  );
}

function RenderModelDocument(): JSX.Element | null {
  const editor = useEditor();

  /**
   * We need to subscribe to editor.onChange counter value here to force a re-render whenever editor.renderModel is updated.
   * TYPING PERF: Do this in a focused RenderModelDocument to avoid re-rendering the entire LayoutEngine subtree.
   */
  useEditorOnChangeCounter();

  if (!editor.renderModel) return null;

  return <Document document={editor.renderModel} path={[0]} />;
}

export function useSetAlineDocPageWidthPx() {
  const {
    viewer: { setViewerPageWidthPx },
  } = useEditorTabStateActions();
  /**
   * `activePageDimensions` reads the editor's current page setup, which can
   * change via Slate ops (page size / margins). The editor reference is stable,
   * so read the width through useSlateSelector — it recomputes reactively on
   * editor changes rather than only when this component happens to re-render.
   */
  const widthPx = useSlateSelector(editor => Queries.activePageDimensions(editor, 'px').width);

  useEffect(() => {
    setViewerPageWidthPx(widthPx);
  }, [setViewerPageWidthPx, widthPx]);
}

interface ClosestPageInfo {
  pointerX: number;
  pointerY: number;
  rMPageEntry: NodeEntry<RenderModelPage>;
}

function getClosestPageToPointer(
  editor: AlineClientEditor,
  options: { clientX: number; clientY: number }
): ClosestPageInfo | null {
  const { clientX, clientY } = options;

  const documentEl = EDITOR_TO_DOCUMENT_ELEMENT.get(editor);
  if (!documentEl) return null;

  let closestPageDistance = Infinity;
  let closestPageInfo: ClosestPageInfo | null = null;

  for (const pageEl of documentEl.querySelectorAll('[data-page]')) {
    if (!(pageEl instanceof HTMLElement) || !pageEl.dataset.pagePath) continue;

    const boundingBox = pageEl.getBoundingClientRect();
    if (!boundingBox) continue;

    const distanceFromTop = boundingBox.top - clientY;
    const distanceFromBottom = clientY - boundingBox.bottom;
    const distance = Math.max(distanceFromTop, distanceFromBottom);

    if (distance < closestPageDistance) {
      const pagePath: Path = JSON.parse(pageEl.dataset.pagePath);
      const rMPageEntry = Queries.renderModelNode(editor, pagePath);
      if (!isRenderModelPageEntry(rMPageEntry)) throw new Error('Expected page entry');

      closestPageDistance = distance;
      closestPageInfo = {
        pointerX: clientX - boundingBox.left,
        pointerY: clientY - boundingBox.top,
        rMPageEntry,
      };
    }
  }

  return closestPageInfo;
}
